/**
 * Change notice (SCN) item — V98-5-3 / D-98-13. A regulator-facing artifact:
 *
 *   Classification  the computed class is a SUGGESTION (the rules behind
 *                   "Show rule"); an operator DECIDES it, with a reason. One
 *                   compare row (Suggested / Decided, "Differs"), co-approval
 *                   and the ADVISORY assessor view as meta rows (V98-5 review
 *                   T5-H3). Once decided, the form folds behind "Change the
 *                   decision…" (warns that a co-approval is withdrawn and asked
 *                   again; Save stays disabled until the class changes, T5-H11).
 *   Notices         each notice, its due date and how that date was set (two
 *                   human lines; the rule behind "Show rule", T5-B6).
 *   Status          planned start (set / move — the notices recompute) and the
 *                   moves the server allows next (Record as started / completed
 *                   / verified, Cancel the change…), each with a date (T5-B2).
 *   Draft           generated from the boundary; "Edit draft" opens a form held
 *                   in local state with an explicit "Save draft" (never saved
 *                   on change, T5-H16); every contradiction links to the field
 *                   it names, or to Scope (with a way back); Regenerate confirms
 *                   when the draft has edits.
 *   Record as sent  GAC Pro has no channel to FedRAMP or the agencies (T5-I2):
 *                   the sender downloads the notice, delivers it and records how
 *                   and when; the confirm shows the whole draft, the due date
 *                   and how it was set, and the server keeps the exact
 *                   downloaded bytes + their sha256 on the append-only record.
 *
 * Reads: GET /significant-changes/:id (any member). Writes: operators only
 * (owner/admin, the program lead, the change owner) — the server decides; this
 * page hides what the viewer can't do and says who can.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Field, Input, Radio, Select, Spinner, Textarea } from '@aigovops/design-system';
import type { ScnContradiction, ScnNotice } from '@aigovops/core';
import {
  classifyScn, downloadScnNotice, getScn, recordScnAssessorView, saveScnDraft, sendScnNotice, setScnPlannedStart, transitionScn, type ScnDetailView,
} from '../../lib/cert-ops-api';
import { SCN_CLASS_ORDER, fmtDate, howDeadlineWasSet, noticeWord, scnClassWord } from '../../lib/cert-ops-model';
import { usePrograms } from '../../lib/workspace-navigation';
import { SCN_PROGRAM } from '../../lib/nav-config';
import { firstName, nameOf, type Names } from '../../lib/today-model';
import { AskAdmin } from '../AskAdmin';
import { ActionDialog, Chip, JumpLink, errorText } from '../today/shared';

type Cls = 'routine' | 'adaptive' | 'transformative';
const CLASSES: readonly Cls[] = SCN_CLASS_ORDER;
const DAY = 86_400_000;

export function ScnItem({ slug, prefix, id, me, names, onDone }: {
  slug: string; prefix: string; id: string; me: string; names: Names; onDone: (msg: string, focusId: string) => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['scn', slug, id], queryFn: () => getScn(slug, id), retry: false });
  const programs = usePrograms(slug);
  if (q.isLoading) return <Spinner label="Loading the change" />;
  if (q.error || !q.data) {
    return <Alert variant="error"><p>The change could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  }
  const d = q.data;
  const programKey = programs.data?.programs.find((p) => p.profile_id === d.change.profile_id)?.program ?? SCN_PROGRAM ?? '';
  const refresh = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['scn', slug, id] }), qc.invalidateQueries({ queryKey: ['today', slug] }), qc.invalidateQueries({ queryKey: ['package-drafts', slug] })]);
  };
  return (
    <>
      <ClassificationBlock slug={slug} prefix={prefix} d={d} names={names} refresh={refresh} onDone={onDone} />
      <NoticesBlock d={d} names={names} />
      <StatusBlock slug={slug} d={d} refresh={refresh} onDone={onDone} />
      <DraftBlock slug={slug} prefix={prefix} programKey={programKey} d={d} refresh={refresh} onDone={onDone} />
      <SendBlock slug={slug} prefix={prefix} d={d} me={me} names={names} refresh={refresh} onDone={onDone} />
    </>
  );
}

function Block({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return <section className="gp-hat-block" aria-labelledby={id}><h2 id={id} tabIndex={-1}>{title}</h2>{children}</section>;
}

// ---- classification ------------------------------------------------------------------

function ClassificationBlock({ slug, prefix, d, names, refresh, onDone }: {
  slug: string; prefix: string; d: ScnDetailView; names: Names; refresh: () => Promise<void>; onDone: (m: string, f: string) => void;
}) {
  const c = d.change;
  const sent = c.notices.some((n) => n.sent_at);
  const [choice, setChoice] = useState<Cls>((c.decided_class ?? c.suggested_class) as Cls);
  const [why, setWhy] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assessorOpen, setAssessorOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const departing = choice !== c.suggested_class;
  const min = departing ? 20 : 10;
  const co = d.co_approval;
  const unchanged = !!c.decided_class && choice === c.decided_class;
  const differs = !!c.decided_class && c.decided_class !== c.suggested_class;
  const showForm = d.can_operate && !sent && c.status !== 'cancelled' && (!c.decided_class || changing);
  useEffect(() => { if (changing) document.getElementById(`scn-class-${choice}`)?.focus(); }, [changing]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (unchanged) return;
    if (why.trim().length < min) {
      setFieldErr(departing ? `You’re departing from the suggestion: say why in at least ${min} characters.` : `Say why in at least ${min} characters.`);
      document.getElementById('scn-why')?.focus();
      return;
    }
    setBusy(true); setErr(null);
    try {
      await classifyScn(slug, c.change_id, { decided_class: choice, rationale: why.trim() });
      await refresh();
      setWhy(''); setChanging(false);
      onDone(`Recorded as ${scnClassWord(choice).toLowerCase()}`, 'scn-decision');
    } catch (e) { setErr(errorText(e, 'The classification could not be recorded.')); }
    finally { setBusy(false); }
  };
  const coRequest = co.request_id && (co.state === 'pending' || co.state === 'returned' || co.routing_status === 'none_eligible')
    ? <Link to={`${prefix}/items/signature_request/${encodeURIComponent(co.request_id)}?from=today`}>{co.routing_status === 'none_eligible' ? 'Find a co-approver' : 'Open the co-approval'}</Link> : null;
  return (
    <Block id="scn-class-h" title="Classification">
      <dl className="gp-dl gp-compare" data-part="scn-compare">
        <dt>Suggested</dt>
        <dd data-part="scn-suggested">
          <span>{`${scnClassWord(c.suggested_class)} · from your answers`}</span>
          {c.evaluation.rule_ids.length ? (
            <details className="gp-how"><summary>Show rule</summary><ul className="gp-plain-list">{c.evaluation.rule_ids.map((r) => <li key={r}>{r}</li>)}</ul></details>
          ) : null}
        </dd>
        <dt>Decided</dt>
        <dd id="scn-decision" tabIndex={-1} data-part="scn-decided">
          {c.decided_class ? (
            <span className="gp-ow-meta gp-ow-meta--text">
              <strong>{scnClassWord(c.decided_class)}</strong>
              <span>{`by ${nameOf(names, c.decided_by_user_id)} · ${fmtDate(c.decided_at)}`}</span>
              {differs ? <Chip text="Differs" tone="neutral" /> : null}
            </span>
          ) : <span>Not decided yet</span>}
          {c.decision_rationale ? <span className="gp-dim">{c.decision_rationale}</span> : null}
        </dd>
        {c.ai_model_change ? <><dt>Co-approval</dt><dd data-part="scn-co-approval"><span>{coLine(co, names)}</span>{coRequest ? <> {coRequest}</> : null}</dd></> : null}
        <dt>Assessor’s view</dt>
        <dd data-part="scn-assessor">
          {c.assessor_view
            ? <span>{`${c.assessor_view.assessor_name} thinks ${scnClassWord(c.assessor_view.classification).toLowerCase()} · advisory, doesn’t co-approve${c.assessor_view.note ? ` · ${c.assessor_view.note}` : ''}`}</span>
            : <span className="gp-dim">None recorded · optional, advisory</span>}
          {d.can_operate ? <> <Button compact variant="ghost" id="scn-assessor-open" onClick={() => setAssessorOpen(true)}>{c.assessor_view ? 'Update…' : 'Record…'}</Button></> : null}
        </dd>
      </dl>

      {d.can_operate && c.decided_class && !sent && c.status !== 'cancelled' ? (
        <div className="gp-actions">
          <Button variant="ghost" ref={toggle} aria-expanded={changing} aria-controls="scn-decide-form" onClick={() => { setChanging((x) => !x); setErr(null); setFieldErr(null); }}>Change the decision…</Button>
        </div>
      ) : null}
      {showForm ? (
        <form id="scn-decide-form" className="gp-form gp-form--narrow" noValidate aria-labelledby="scn-decide-h" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <h3 id="scn-decide-h" className="gp-sec-h" tabIndex={-1}>{c.decided_class ? 'Change the decision' : 'Decide the class'}</h3>
          {err ? <Alert variant="error">{err}</Alert> : null}
          {c.decided_class && co.request_id && (co.state === 'pending' || co.state === 'approved') ? (
            <p className="gp-state gp-state--warn" data-part="scn-co-withdraw">{`Changing it withdraws ${co.approver_user_id ? firstName(nameOf(names, co.approver_user_id)) : 'the co-approver'}’s co-approval${co.state === 'approved' ? '' : ' request'} and asks again.`}</p>
          ) : null}
          <fieldset className="gp-form" aria-describedby="scn-class-hint">
            <legend className="gp-sr-only">Class</legend>
            {CLASSES.map((k) => (
              <Radio key={k} id={`scn-class-${k}`} name="scn-class" value={k} checked={choice === k} onChange={() => { setChoice(k); setFieldErr(null); }}
                label={k === c.suggested_class ? `${scnClassWord(k)} · suggested` : k === c.decided_class ? `${scnClassWord(k)} · decided` : scnClassWord(k)}
                description={k === 'adaptive' && c.ai_model_change ? 'Needs a co-approver' : k === 'transformative' ? 'Files “engage the assessor” and a finding' : undefined} />
            ))}
          </fieldset>
          <p id="scn-class-hint" className="gp-dim">Recorded with your name and the time.</p>
          <Field id="scn-why" label="Why" required hint={departing ? 'At least 20 characters when you depart from the suggestion' : 'At least 10 characters'} error={fieldErr ?? undefined}>
            <Textarea rows={3} maxLength={4000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setFieldErr(null); setErr(null); }} />
          </Field>
          <div className="gp-actions">
            <Button type="submit" variant={c.decided_class ? 'secondary' : 'primary'} aria-disabled={busy || unchanged || undefined} aria-describedby={unchanged ? 'scn-unchanged' : undefined}>{busy ? 'Recording…' : 'Record classification'}</Button>
            {c.decided_class ? <Button variant="ghost" onClick={() => { setChanging(false); setChoice(c.decided_class as Cls); setWhy(''); toggle.current?.focus(); }}>Cancel</Button> : null}
          </div>
          {unchanged ? <p id="scn-unchanged" className="gp-dim">Choose a different class to change the decision.</p> : null}
        </form>
      ) : !d.can_operate && !c.decided_class ? (
        <p className="gp-dim">The program lead or an admin decides the class. <AskAdmin slug={slug} compact verb="classify it" what="classifies change notices" subject={`GAC Pro: classify the change “${c.title}”`} /></p>
      ) : null}

      {d.engagement ? (
        <div className="gp-state gp-state--warn" data-part="scn-engage">
          <strong>Transformative · engage the assessor</strong>
          <span className="gp-ow-meta">
            {d.engagement.work_item_id ? <Link to={`${prefix}/items/work_item/${encodeURIComponent(d.engagement.work_item_id)}?from=today`}>Open “Engage the assessor”</Link> : null}
            {d.engagement.finding_id ? <Link to={`${prefix}/findings?finding=${encodeURIComponent(d.engagement.finding_id)}`}>Open the finding</Link> : null}
          </span>
        </div>
      ) : null}

      <AssessorDialog open={assessorOpen} slug={slug} id={c.change_id} initial={c.assessor_view} onClose={() => setAssessorOpen(false)}
        onDone={async (m) => { setAssessorOpen(false); await refresh(); onDone(m, 'scn-assessor-open'); }} />
    </Block>
  );
}

function coLine(co: ScnDetailView['co_approval'], names: Names): string {
  const who = co.approver_user_id ? firstName(nameOf(names, co.approver_user_id)) : null;
  switch (co.state) {
    case 'not_required': return 'Not needed';
    case 'not_requested': return 'Requested when you record the decision';
    case 'pending': return co.routing_status === 'none_eligible' ? 'Nobody eligible yet' : `Waiting on ${who ?? 'the co-approver'}`;
    case 'approved': return `Co-approved by ${nameOf(names, co.signed_by_user_id)} · ${fmtDate(co.signed_at)}`;
    case 'returned': return `Sent back by ${who ?? 'the co-approver'}${co.reason ? ` · ${co.reason}` : ''}`;
    default: return '';
  }
}

function AssessorDialog({ open, slug, id, initial, onClose, onDone }: {
  open: boolean; slug: string; id: string; initial: ScnDetailView['change']['assessor_view']; onClose: () => void; onDone: (m: string) => void;
}) {
  const [who, setWho] = useState(initial?.assessor_name ?? '');
  const [cls, setCls] = useState<Cls>((initial?.classification ?? 'adaptive') as Cls);
  const [note, setNote] = useState(initial?.note ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [whoErr, setWhoErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={open} title="The assessor’s view" submitLabel="Record view" busy={busy} error={err} onClose={onClose}
      onSubmit={async () => {
        if (!who.trim()) { setWhoErr('Name the assessor.'); document.getElementById('scn-assessor-name')?.focus(); return false; }
        setBusy(true); setErr(null);
        try { await recordScnAssessorView(slug, id, { assessor_name: who.trim(), classification: cls, note: note.trim() }); onDone('Assessor’s view recorded · advisory'); return true; }
        catch (e) { setErr(errorText(e, 'The view could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      <p className="gp-dim">Advisory: it’s shown beside the decision and never co-approves it.</p>
      <Field id="scn-assessor-name" label="Assessor" required error={whoErr ?? undefined}>
        <Input value={who} maxLength={200} onChange={(e) => { setWho(e.currentTarget.value); setWhoErr(null); }} />
      </Field>
      <Field id="scn-assessor-class" label="Their view">
        <Select value={cls} onChange={(e) => setCls(e.currentTarget.value as Cls)}>
          {CLASSES.map((k) => <option key={k} value={k}>{scnClassWord(k)}</option>)}
        </Select>
      </Field>
      <Field id="scn-assessor-note" label="What they said" hint="Optional">
        <Textarea rows={3} maxLength={4000} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}

// ---- notices -----------------------------------------------------------------------

function NoticesBlock({ d, names }: { d: ScnDetailView; names: Names }) {
  const notices = d.change.notices;
  return (
    <Block id="scn-notices-h" title="Notices">
      {notices.length === 0 ? <p className="gp-dim">{d.change.decided_class === 'routine' ? 'Routine changes carry no notice.' : 'No notice for this class.'}</p> : (
        <ul className="gp-owed-list gp-owed-list--flat" aria-label="Notices">
          {notices.map((n) => <NoticeRow key={n.rule_id} n={n} names={names} />)}
        </ul>
      )}
    </Block>
  );
}

function NoticeRow({ n, names }: { n: ScnNotice; names: Names }) {
  const how = howDeadlineWasSet({ rule_id: n.rule_id, rule_version: n.rule_version, verification: n.verification, calendar: n.calendar });
  const now = Date.now();
  const chip = n.sent_at ? { text: 'Recorded as sent', tone: 'ok' as const }
    : n.due_at ? { text: `Due ${fmtDate(n.due_at)}`, tone: Date.parse(n.due_at) < now ? 'red' as const : Date.parse(n.due_at) - now <= 14 * DAY ? 'amber' as const : 'neutral' as const }
      : { text: n.status === 'awaiting_anchor' ? (n.anchor === 'completed' ? 'Due after the change is completed' : n.anchor === 'verified' ? 'Due after verification' : 'Set the planned start') : 'Date pending the rule', tone: 'neutral' as const };
  return (
    <li className="gp-ow gp-ow--wait" data-notice={n.rule_id}>
      <span className="gp-ow-main">
        <span className="gp-ow-t">{noticeWord(n.rule_id)}</span>
        <span className="gp-ow-meta">
          <Chip text={chip.text} tone={chip.tone} />
          {n.sent_at ? <span>{`${fmtDate(n.sent_at)}${n.sent_by_user_id ? ` · by ${nameOf(names, n.sent_by_user_id)}` : ''}`}</span> : null}
          <span className="gp-chip gp-chip--neutral" title={how.title ?? undefined}>{how.label}</span>
        </span>
        <details className="gp-how">
          <summary>How the deadline was set<span className="gp-sr-only">{` · ${noticeWord(n.rule_id)}`}</span></summary>
          <ul className="gp-plain-list">{how.lines.map((l) => <li key={l}>{l}</li>)}</ul>
          {how.rule.length ? (
            <details className="gp-how gp-how--nested"><summary>Show rule</summary><ul className="gp-plain-list">{how.rule.map((l) => <li key={l}>{l}</li>)}</ul></details>
          ) : null}
        </details>
      </span>
    </li>
  );
}

// ---- status: planned start + the moves the server allows (T5-B2) ----------------------

const MOVE: Record<string, { label: string; done: string; danger?: boolean }> = {
  in_progress: { label: 'Record as started', done: 'Change recorded as started' },
  completed: { label: 'Record as completed', done: 'Change recorded as completed' },
  verified: { label: 'Record as verified', done: 'Change recorded as verified' },
  cancelled: { label: 'Cancel the change…', done: 'Change cancelled', danger: true },
};
const STATUS_WORD: Record<string, string> = { planned: 'Planned', in_progress: 'In progress', completed: 'Completed', verified: 'Verified', cancelled: 'Cancelled' };

function StatusBlock({ slug, d, refresh, onDone }: { slug: string; d: ScnDetailView; refresh: () => Promise<void>; onDone: (m: string, f: string) => void }) {
  const c = d.change;
  const [move, setMove] = useState<'in_progress' | 'completed' | 'verified' | 'cancelled' | null>(null);
  const [planned, setPlanned] = useState(c.planned_start ? c.planned_start.slice(0, 10) : '');
  const [plannedErr, setPlannedErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changedPlanned = planned !== (c.planned_start ? c.planned_start.slice(0, 10) : '');
  const savePlanned = async () => {
    if (!planned) { setPlannedErr('Choose the planned start.'); document.getElementById('scn-planned')?.focus(); return; }
    setBusy(true); setErr(null);
    try { await setScnPlannedStart(slug, c.change_id, `${planned}T00:00:00.000Z`); await refresh(); onDone(`Planned start ${fmtDate(planned)} · notices recomputed`, 'scn-notices-h'); }
    catch (e) { setErr(errorText(e, 'The planned start could not be saved.')); }
    finally { setBusy(false); }
  };
  if (!d.can_operate && c.status !== 'cancelled') {
    return <Block id="scn-status-h" title="Status"><p>{`${STATUS_WORD[c.status] ?? c.status}${c.planned_start ? ` · planned start ${fmtDate(c.planned_start)}` : ''}`}</p></Block>;
  }
  return (
    <Block id="scn-status-h" title="Status">
      <p data-part="scn-status">{[STATUS_WORD[c.status] ?? c.status, c.planned_start ? `planned start ${fmtDate(c.planned_start)}` : null,
        c.completed_at ? `completed ${fmtDate(c.completed_at)}` : null, c.verified_at ? `verified ${fmtDate(c.verified_at)}` : null].filter(Boolean).join(' · ')}</p>
      {err ? <Alert variant="error">{err}</Alert> : null}
      {d.can_operate && d.can_set_planned_start ? (
        <form className="gp-inline-row" noValidate aria-label="Planned start" onSubmit={(e) => { e.preventDefault(); void savePlanned(); }}>
          <Field id="scn-planned" label="Planned start" hint="Notices before the change count back from it" error={plannedErr ?? undefined}>
            <Input type="date" value={planned} onChange={(e) => { setPlanned(e.currentTarget.value); setPlannedErr(null); }} />
          </Field>
          <Button type="submit" variant="secondary" aria-disabled={busy || !changedPlanned || undefined}>Save planned start</Button>
        </form>
      ) : null}
      {d.can_operate && d.transitions.length ? (
        <div className="gp-actions" data-part="scn-moves">
          {d.transitions.map((t) => <Button key={t} variant={t === 'cancelled' ? 'ghost' : 'secondary'} id={`scn-move-${t}`} onClick={() => setMove(t)}>{MOVE[t]!.label}</Button>)}
        </div>
      ) : null}
      <MoveDialog slug={slug} d={d} move={move} onClose={() => setMove(null)}
        onDone={async (m) => { const t = move; setMove(null); await refresh(); onDone(m, t ? 'scn-status-h' : 'scn-status-h'); }} />
    </Block>
  );
}

function MoveDialog({ slug, d, move, onClose, onDone }: {
  slug: string; d: ScnDetailView; move: 'in_progress' | 'completed' | 'verified' | 'cancelled' | null; onClose: () => void; onDone: (m: string) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [at, setAt] = useState(today);
  const [reason, setReason] = useState('');
  const [errs, setErrs] = useState<{ at?: string; reason?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const m = move ? MOVE[move]! : null;
  return (
    <ActionDialog open={!!move} title={m ? m.label.replace('…', '') : ''} submitLabel={m ? m.label.replace('…', '') : ''} danger={m?.danger} busy={busy} error={err}
      cancelLabel={move === 'cancelled' ? 'Keep the change' : 'Cancel'}
      onClose={() => { setAt(today); setReason(''); setErrs({}); setErr(null); onClose(); }}
      onSubmit={async () => {
        if (!move) return false;
        // V98-7-3 L6: a cancel records why (server-stamped, no date to pick).
        if (move === 'cancelled' && reason.trim().length < 10) { setErrs({ reason: 'Say why (at least 10 characters).' }); document.getElementById('scn-move-reason')?.focus(); return false; }
        if (move !== 'cancelled' && (!at || at > today)) { setErrs({ at: 'Today or earlier.' }); document.getElementById('scn-move-at')?.focus(); return false; }
        setBusy(true); setErr(null);
        try {
          await transitionScn(slug, d.change.change_id, move === 'cancelled' ? { to: move, reason: reason.trim() } : { to: move, at: `${at}T12:00:00.000Z` });
          setAt(today); setReason(''); await onDone(m!.done); return true;
        }
        catch (x) { setErr(errorText(x, 'It could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      {move === 'cancelled' ? <p>Notices not yet recorded as sent are dropped. This can’t be undone.</p> : null}
      {move === 'cancelled' ? (
        <Field id="scn-move-reason" label="Why" required error={errs.reason}>
          <Textarea rows={3} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setErrs({}); }} />
        </Field>
      ) : (
        <Field id="scn-move-at" label="Date" required error={errs.at}>
          <Input type="date" max={today} value={at} onChange={(e) => { setAt(e.currentTarget.value); setErrs({}); }} />
        </Field>
      )}
    </ActionDialog>
  );
}

// ---- draft --------------------------------------------------------------------------

/** Which field a contradiction names (its control id in the edit form). */
const FIX_FIELD: Partial<Record<ScnContradiction['code'], 'scope' | 'regenerate' | 'decide' | string>> = {
  provider_not_listed: 'scope', system_not_in_boundary: 'scope', provider_already_listed: 'provider', federal_data_understated: 'scn-federal',
  data_flow_changed: 'scn-flows', class_mismatch: 'regenerate', not_decided: 'decide', provider_omitted: 'regenerate', system_omitted: 'regenerate',
};

type DraftForm = { what: string; claims: Record<string, 'listed' | 'new'>; flows: 'unchanged' | 'changed'; federal: 'none' | 'present' };

function DraftBlock({ slug, prefix, programKey, d, refresh, onDone }: {
  slug: string; prefix: string; programKey: string; d: ScnDetailView; refresh: () => Promise<void>; onDone: (m: string, f: string) => void;
}) {
  const draft = d.draft.draft;
  const initial = (): DraftForm => ({ what: draft.what_changed, claims: Object.fromEntries(draft.providers.map((p) => [p.provider_key, p.claim])), flows: draft.data_flows, federal: draft.federal_data });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<DraftForm>(initial);
  const [whatErr, setWhatErr] = useState<string | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const operator = d.can_operate && !d.change.notices.every((n) => n.sent_at);
  const base = initial();
  const dirty = form.what !== base.what || form.flows !== base.flows || form.federal !== base.federal || draft.providers.some((p) => form.claims[p.provider_key] !== p.claim);
  const ret = encodeURIComponent(`${prefix}/items/scn/${encodeURIComponent(d.change.change_id)}?from=program:${programKey}`);
  const scope = `${prefix}/programs/${programKey}/scope?return=${ret}`;
  // The Send section already lists "not decided": the draft's count leaves it out.
  const contradictions = d.draft.contradictions.filter((x) => !(x.code === 'not_decided' && d.send_gate.blockers.some((b) => b.code === 'not_decided')));
  const open = (focusId?: string) => {
    setForm(initial()); setEditing(true); setWhatErr(null);
    requestAnimationFrame(() => document.getElementById(focusId ?? 'scn-what')?.focus());
  };
  const close = () => { setEditing(false); setForm(initial()); setWhatErr(null); toggle.current?.focus(); };
  const save = async () => {
    if (!form.what.trim()) { setWhatErr('Say what changed.'); document.getElementById('scn-what')?.focus(); return; }
    setBusy(true); setErr(null);
    try {
      const claims = Object.fromEntries(draft.providers.filter((p) => form.claims[p.provider_key] !== p.claim).map((p) => [p.provider_key, form.claims[p.provider_key]!]));
      await saveScnDraft(slug, d.change.change_id, {
        ...(form.what !== base.what ? { what_changed: form.what.trim() } : {}), ...(Object.keys(claims).length ? { provider_claims: claims } : {}),
        ...(form.flows !== base.flows ? { data_flows: form.flows } : {}), ...(form.federal !== base.federal ? { federal_data: form.federal } : {}),
      });
      await refresh();
      setEditing(false);
      toggle.current?.focus();
      onDone('Draft saved', 'scn-draft-edit');
    } catch (e) { setErr(errorText(e, 'The draft could not be saved.')); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    setBusy(true); setErr(null);
    try { await saveScnDraft(slug, d.change.change_id, { regenerate: true }); await refresh(); setEditing(false); onDone('Draft regenerated from the boundary', 'scn-draft-h'); return true; }
    catch (e) { setErr(errorText(e, 'The draft could not be regenerated.')); return false; }
    finally { setBusy(false); }
  };
  const askRegenerate = () => (d.draft.edited ? setConfirmRegen(true) : void regenerate());
  const fixLink = (x: ScnContradiction) => {
    const f = FIX_FIELD[x.code];
    if (f === 'scope') return <Link to={scope}>Fix in Scope</Link>;
    if (f === 'regenerate') return operator ? <Button compact variant="ghost" onClick={askRegenerate}>Regenerate</Button> : null;
    if (f === 'decide') return <JumpLink to="scn-class-h">Decide the class</JumpLink>;
    if (!operator || !f) return null;
    const target = f === 'provider' ? `scn-claim-${x.field.replace('providers.', '')}` : f;
    return <button type="button" className="gp-textlink" onClick={() => open(target)}>{f === 'scn-federal' ? 'Change “Federal data”' : f === 'scn-flows' ? 'Change “Data flows”' : 'Change the claim'}</button>;
  };
  return (
    <Block id="scn-draft-h" title="Notice draft">
      <p className="gp-dim">{`Drafted from the boundary · ${d.draft.edited ? 'edited' : 'not edited'} · to ${draft.recipients.join(', ')}`}</p>
      {contradictions.length ? (
        <div className="gp-state gp-state--warn" data-part="scn-contradictions" role="group" aria-labelledby="scn-contra-h">
          <strong id="scn-contra-h">{`Contradicts the boundary · ${contradictions.length}`}</strong>
          <ul className="gp-plain-list">
            {contradictions.map((x) => <li key={`${x.code}:${x.field}`}>{x.message}{' '}{fixLink(x)}</li>)}
          </ul>
        </div>
      ) : <p className="gp-dim" data-part="scn-agrees">Agrees with the boundary.</p>}
      {err ? <Alert variant="error">{err}</Alert> : null}
      {!editing ? (
        <dl className="gp-dl" data-part="scn-draft">
          <dt>Class</dt><dd>{scnClassWord(draft.classification)}</dd>
          <dt>What changed</dt><dd>{draft.what_changed}</dd>
          <dt>Systems</dt><dd>{draft.systems.map((s) => s.name).join(', ') || 'None named'}</dd>
          <dt>Providers</dt><dd>{draft.providers.map((p) => `${p.name} · ${p.claim === 'listed' ? 'listed external service' : 'new provider'}`).join(', ') || 'None'}</dd>
          <dt>Data flows</dt><dd>{draft.data_flows === 'unchanged' ? 'Unchanged' : 'Changed'}</dd>
          <dt>Federal data</dt><dd>{draft.federal_data === 'present' ? 'Involved' : 'Not involved'}</dd>
        </dl>
      ) : null}
      {operator ? (
        <div className="gp-actions">
          <Button variant="secondary" id="scn-draft-edit" ref={toggle} aria-expanded={editing} aria-controls="scn-draft-form" onClick={() => (editing ? close() : open())}>Edit draft</Button>
          {d.draft.edited && !editing ? <Button variant="ghost" onClick={askRegenerate}>Regenerate from the boundary</Button> : null}
        </div>
      ) : null}
      {editing ? (
        <form id="scn-draft-form" className="gp-form gp-form--narrow" noValidate aria-label="Edit the notice draft" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <Field id="scn-what" label="What changed" required error={whatErr ?? undefined}>
            <Textarea rows={3} maxLength={4000} value={form.what} onChange={(e) => { setForm({ ...form, what: e.currentTarget.value }); setWhatErr(null); }} />
          </Field>
          <dl className="gp-dl gp-draft-grid">
            {draft.providers.map((p) => (
              <FragmentRow key={p.provider_key} label={p.name} htmlFor={`scn-claim-${p.provider_key}`}>
                <Select id={`scn-claim-${p.provider_key}`} className="gp-select-fit" aria-label={`What the draft says about ${p.name}`} value={form.claims[p.provider_key]}
                  onChange={(e) => setForm({ ...form, claims: { ...form.claims, [p.provider_key]: e.currentTarget.value as 'listed' | 'new' } })}>
                  <option value="listed">Listed external service</option><option value="new">New provider</option>
                </Select>
              </FragmentRow>
            ))}
            <FragmentRow label="Data flows" htmlFor="scn-flows">
              <Select id="scn-flows" className="gp-select-fit" value={form.flows} onChange={(e) => setForm({ ...form, flows: e.currentTarget.value as DraftForm['flows'] })}>
                <option value="unchanged">Unchanged</option><option value="changed">Changed</option>
              </Select>
            </FragmentRow>
            <FragmentRow label="Federal data" htmlFor="scn-federal">
              <Select id="scn-federal" className="gp-select-fit" value={form.federal} onChange={(e) => setForm({ ...form, federal: e.currentTarget.value as DraftForm['federal'] })}>
                <option value="present">Involved</option><option value="none">Not involved</option>
              </Select>
            </FragmentRow>
          </dl>
          <div className="gp-actions">
            <Button type="submit" variant="secondary" aria-disabled={busy || !dirty || undefined}>{busy ? 'Saving…' : 'Save draft'}</Button>
            <Button variant="ghost" onClick={close}>Cancel</Button>
          </div>
        </form>
      ) : null}
      <ActionDialog open={confirmRegen} title="Regenerate the draft?" submitLabel="Regenerate" danger busy={busy} onClose={() => setConfirmRegen(false)}
        onSubmit={async () => { const ok = await regenerate(); if (ok) setConfirmRegen(false); return ok; }}>
        <p>Your edits to the draft are replaced with what the boundary says now.</p>
      </ActionDialog>
    </Block>
  );
}

function FragmentRow({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return <><dt><label htmlFor={htmlFor}>{label}</label></dt><dd>{children}</dd></>;
}

// ---- record as sent ------------------------------------------------------------------

const METHOD_WORD: Record<string, string> = { email: 'Email', portal: 'Portal', other: 'Other' };
const todayDay = () => new Date().toISOString().slice(0, 10);

function SendBlock({ slug, prefix, d, me, names, refresh, onDone }: {
  slug: string; prefix: string; d: ScnDetailView; me: string; names: Names; refresh: () => Promise<void>; onDone: (m: string, f: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const n = d.next_notice;
  const sends = d.sends;
  const gate = d.send_gate;
  const completeNow = async () => {
    setBusy(true); setErr(null);
    try { await transitionScn(slug, d.change.change_id, { to: 'completed' }); await refresh(); onDone('Change recorded as completed', 'scn-send-h'); }
    catch (e) { setErr(errorText(e, 'Could not record the completion.')); }
    finally { setBusy(false); }
  };
  const blockedBy = gate.blockers.length === 1 ? '1 thing blocks this' : `${gate.blockers.length} things block this`;
  return (
    <Block id="scn-send-h" title="Record as sent">
      {sends.length ? (
        <ul className="gp-owed-list gp-owed-list--flat" aria-label="Recorded notices" data-part="scn-sends">
          {sends.map((s) => (
            <li key={s.send_id} className="gp-ow gp-ow--wait"><span className="gp-ow-main">
              <span className="gp-ow-t">{noticeWord(s.rule_id)}</span>
              <span className="gp-ow-meta">
                <span>{`Recorded as sent by ${nameOf(names, s.sent_by_user_id)}${s.delivery_method ? ` · ${s.delivery_method === 'other' && s.delivery_note ? s.delivery_note : METHOD_WORD[s.delivery_method]}` : ''} · ${fmtDate(s.delivered_on ?? s.sent_at)}`}</span>
                <span>{`To ${s.recipients.join(', ')}`}</span>
              </span>
              <span className="gp-ow-meta"><DownloadLinks slug={slug} id={d.change.change_id} ruleId={s.rule_id} onError={setErr} /></span>
            </span></li>
          ))}
        </ul>
      ) : null}
      {err ? <Alert variant="error">{err}</Alert> : null}
      {!n ? <p className="gp-dim">{sends.length ? 'Every notice is recorded as sent.' : 'Nothing to send.'}</p>
        : !d.can_operate ? <p className="gp-dim">{gate.ready ? 'Ready · the program lead or an admin records it as sent.' : 'The program lead or an admin records it as sent once nothing blocks it.'}</p>
        : (
          <div className="gp-form" data-part="scn-send-gate">
            <div className="gp-actions">
              <Button variant="primary" id="scn-send-open" aria-disabled={!gate.ready || undefined} aria-describedby={gate.ready ? undefined : 'scn-send-blocked'}
                onClick={() => { if (!gate.ready) return; setErr(null); setOpen(true); }}>Record as sent…</Button>
            </div>
            {!gate.ready ? (
              <div id="scn-send-blocked">
                <p className="gp-dim">{blockedBy}</p>
                <ul className="gp-plain-list">
                  {gate.blockers.map((b) => (
                    <li key={b.code}>
                      {b.message}{' '}
                      {b.code === 'not_decided' ? <a href="#scn-decide-h">Decide the class</a>
                        : (b.code === 'co_approval_pending' || b.code === 'co_approval_returned') && d.co_approval.request_id
                          ? <Link to={`${prefix}/items/signature_request/${encodeURIComponent(d.co_approval.request_id)}?from=today`}>Open the co-approval</Link>
                          : b.code === 'draft_contradicts_boundary' ? <a href="#scn-draft-h">See the contradictions</a>
                            : b.code === 'notice_not_due' && n.anchor === 'completed' ? <Button compact aria-disabled={busy || undefined} onClick={() => void completeNow()}>Record the change as completed</Button>
                              : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      {n ? <RecordSentDialog open={open} slug={slug} d={d} me={me} names={names} onClose={() => setOpen(false)}
        onDone={async (m) => { setOpen(false); await refresh(); onDone(m, 'scn-send-h'); }} /> : null}
    </Block>
  );
}

function DownloadLinks({ slug, id, ruleId, onError }: { slug: string; id: string; ruleId: string; onError: (m: string | null) => void }) {
  const get = async (f: 'json' | 'html') => {
    onError(null);
    try { await downloadScnNotice(slug, id, ruleId, f); } catch (e) { onError(errorText(e, 'The notice could not be downloaded.')); }
  };
  return <>
    <Button compact variant="ghost" onClick={() => void get('json')}>Download notice</Button>
    <Button compact variant="ghost" onClick={() => void get('html')}>Printable copy</Button>
  </>;
}

function RecordSentDialog({ open, slug, d, me, names, onClose, onDone }: {
  open: boolean; slug: string; d: ScnDetailView; me: string; names: Names; onClose: () => void; onDone: (m: string) => Promise<void>;
}) {
  const n = d.next_notice!;
  const draft = d.draft.draft;
  const how = howDeadlineWasSet({ rule_id: n.rule_id, rule_version: n.rule_version, verification: n.verification, calendar: n.calendar });
  const pending = n.verification === 'verification_pending';
  const [method, setMethod] = useState('');
  const [sentOn, setSentOn] = useState(todayDay());
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [errs, setErrs] = useState<{ method?: string; sentOn?: string; note?: string; ack?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setMethod(''); setSentOn(todayDay()); setNote(''); setAck(false); setErrs({}); setErr(null); };
  const name = noticeWord(n.rule_id);
  return (
    <ActionDialog open={open} title={`Record as sent · ${name}`} submitLabel="Record as sent" busy={busy} error={err}
      onClose={() => { reset(); onClose(); }}
      onSubmit={async () => {
        const e: typeof errs = {};
        if (!method) e.method = 'Choose how you sent it.';
        if (!sentOn) e.sentOn = 'Choose the day it went out.';
        else if (sentOn > todayDay()) e.sentOn = 'It can’t be in the future.';
        if (method === 'other' && note.trim().length < 3) e.note = 'Say how it was sent.';
        if (pending && !ack) e.ack = 'Confirm you checked the due date.';
        setErrs(e);
        if (Object.keys(e).length) {
          document.getElementById(e.method ? 'scn-sent-method' : e.sentOn ? 'scn-sent-on' : e.note ? 'scn-sent-note' : 'scn-sent-ack')?.focus();
          return false;
        }
        setBusy(true); setErr(null);
        try {
          await sendScnNotice(slug, d.change.change_id, n.rule_id, {
            confirm: true, draft_sha256: d.draft.sha256,
            delivery: { method: method as 'email' | 'portal' | 'other', sent_on: sentOn, ...(note.trim() ? { note: note.trim() } : {}) },
            ...(pending ? { verification_ack: true } : {}),
          });
          reset();
          await onDone(`Recorded as sent by ${firstName(nameOf(names, me, 'you'))}`);
          return true;
        } catch (x) { setErr(errorText(x, 'It could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      <p className="gp-dim">GAC Pro doesn’t deliver notices. Download it, send it, then record how.</p>
      <dl className="gp-dl" data-part="scn-confirm">
        <dt>Notice</dt><dd>{name}</dd>
        <dt>Due</dt><dd>{n.due_at ? `${fmtDate(n.due_at)} · ${how.label}` : how.label}</dd>
        <dt>Class</dt><dd>{scnClassWord(d.change.decided_class)}</dd>
        <dt>To</dt><dd>{draft.recipients.join(', ')}</dd>
        <dt>What changed</dt><dd>{draft.what_changed}</dd>
        <dt>Systems</dt><dd>{draft.systems.map((s) => s.name).join(', ') || 'None named'}</dd>
        <dt>Providers</dt><dd>{draft.providers.map((p) => `${p.name} · ${p.claim === 'listed' ? 'listed external service' : 'new provider'}`).join(', ') || 'None'}</dd>
        <dt>Data flows</dt><dd>{draft.data_flows === 'unchanged' ? 'Unchanged' : 'Changed'}</dd>
        <dt>Federal data</dt><dd>{draft.federal_data === 'present' ? 'Involved' : 'Not involved'}</dd>
      </dl>
      <div className="gp-actions"><DownloadLinks slug={slug} id={d.change.change_id} ruleId={n.rule_id} onError={setErr} /></div>
      <div className="gp-form-row">
        <Field id="scn-sent-method" label="How you sent it" required error={errs.method}>
          <Select value={method} onChange={(e) => { setMethod(e.currentTarget.value); setErrs((x) => ({ ...x, method: undefined })); }}>
            <option value="">Choose…</option><option value="email">Email</option><option value="portal">Portal</option><option value="other">Other</option>
          </Select>
        </Field>
        <Field id="scn-sent-on" label="Date sent" required error={errs.sentOn}>
          <Input type="date" max={todayDay()} value={sentOn} onChange={(e) => { setSentOn(e.currentTarget.value); setErrs((x) => ({ ...x, sentOn: undefined })); }} />
        </Field>
      </div>
      <Field id="scn-sent-note" label="Sent to or reference" required={method === 'other'} hint="An address or a portal reference" error={errs.note}>
        <Input value={note} maxLength={500} onChange={(e) => { setNote(e.currentTarget.value); setErrs((x) => ({ ...x, note: undefined })); }} />
      </Field>
      {pending ? (
        <div className="gp-state gp-state--warn" data-part="scn-sent-pending">
          <strong>The due date is a placeholder until the FedRAMP rules are verified</strong>
          <Checkbox id="scn-sent-ack" checked={ack} required aria-invalid={errs.ack ? true : undefined} aria-describedby={errs.ack ? 'scn-sent-ack-err' : undefined}
            onChange={(e) => { setAck(e.currentTarget.checked); setErrs((x) => ({ ...x, ack: undefined })); }} label="I checked the due date myself" />
          {errs.ack ? <p id="scn-sent-ack-err" className="gp-field-err">{errs.ack}</p> : null}
        </div>
      ) : null}
      <p className="gp-dim">{`Recorded with your name (${nameOf(names, me, 'you')}), the time and the notice exactly as downloaded.`}</p>
    </ActionDialog>
  );
}
